import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import { DefaultFilter, User, UserPreferences, UserSystemAttributes } from '../domain/rbac/RbacDomains';
import { UserClient } from '../client/user/UserClient';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import { query } from '@trlogic/client-js';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import { ActiveRiskType, TransactionRiskType } from '../domain/stream/transaction/TransactionDomains';
import Query = query.Query;

class UserService {
  private userClient: UserClient = GatewayContainer.getInstance().get(GatewayNames.USER_CLIENT);

  public saveUser = async (dto: UserDto): Promise<boolean> => {
    const { password, rePassword, emailValidation } = dto;

    if (dto.username == 'System') {
      MessageUtil.error({
        summary: 'System is reserved username',
      });
      return false;
    }

    const user: User = this.prepareUser(dto);
    const isExist = await this.userIsExist(user);

    if (isExist) {
      MessageUtil.alreadyExist('User already exist');
      return false;
    }
    if (!this.checkPassword(password, rePassword)) {
      return false;
    }
    if (!emailValidation) {
      MessageUtil.checkEmail();
      return false;
    }

    const requestDto = { user, password: password!.replace(' ', '') };
    try {
      await this.userClient.create(requestDto.user, password!);

      MessageUtil.success({
        summary: <div>{LanguageUtil.get({ id: 'messages.user', defaultMessage: 'User' })}</div>,
        detail: <div>{LanguageUtil.get({ id: 'messages.created', defaultMessage: 'created.' })}</div>,
      });

      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.creatingUser', defaultMessage: 'Error when creating user' }),
      });
      return false;
    }
  };

  public async deleteUser(id: string) {
    try {
      await this.userClient.delete(id);
      MessageUtil.deleteMessage('User');
    } catch (e: any) {
      const dependencies: any = e.response.data;
      if (dependencies) {
        dispatch(openBulkActionDialogFromDependency(dependencies, 'User' as any));
        return dependencies;
      }
      MessageUtil.showError('User');
    }
  }

  public updateUser = async (dto: UserUpdateDto): Promise<boolean> => {
    const { user, password, rePassword, emailValidation } = dto;

    if (password != rePassword) {
      MessageUtil.checkPassword();
      return false;
    }

    if (!emailValidation) {
      MessageUtil.checkEmail();
      return false;
    }

    try {
      const dto = { user, password: password?.replace(' ', '') };
      await this.userClient.update(dto.user, dto.password);
      return true;
    } catch (e) {
      return false;
    }
  };

  private getActiveRiskType = (riskType: TransactionRiskType): ActiveRiskType => {
    switch (riskType) {
      case 'all':
        return 'fraud';
      case 'compliance':
        return 'compliance';
      case 'fraud':
        return 'fraud';
    }
  };

  private prepareUser(dto: UserDto): User {
    const { username, firstName, lastName, role, groups, email, roles, preferences } = dto;
    const user: User = {
      username: username!,
      firstName: firstName!,
      lastName: lastName!,
      email: email!,
      role,
      groups,
      userType: 1,
      roles,
      enabled: true,
      activeRiskType: this.getActiveRiskType(dto.riskType),
      preferences: {
        timeZone: preferences.timeZone || undefined,
        twoFA: preferences.twoFA,
        language: preferences.language,
        fontSize: '16px',
      },
      riskType: dto.riskType,
      filters: dto.filters ?? [],
    };
    return user;
  }

  private userIsExist = async (user: User): Promise<boolean> => {
    try {
      await this.userClient.exist(user.username, user.email!);
      return false;
    } catch (e: any) {
      const { message } = e.response.data;
      if (message == 'SAME_USERNAME') {
        MessageUtil.alreadyExist(<div>{LanguageUtil.get({ id: 'admin.user.username', defaultMessage: 'Username' })}</div>);
        return true;
      }
      if (message == 'SAME_EMAIL') {
        MessageUtil.alreadyExist(<div>{LanguageUtil.get({ id: 'admin.user.email', defaultMessage: 'User' })}</div>);
        return true;
      }
      return true;
    }
  };

  public profileUpdate = async (userId: string, user: User) => {
    await this.userClient.profileUpdate(userId, user).catch(() => MessageUtil.showError('User'));
  };

  public updatePreferences = (preferences: UserPreferences) => {
    return this.userClient.updatePreferences(preferences);
  };

  public getCount(query: Query) {
    return this.userClient.getCount(query);
  }

  private checkPassword(password: string | undefined, rePassword: string | undefined) {
    if (password == undefined) {
      MessageUtil.error({ summary: 'Password can not be empty' });
      return false;
    } else if (rePassword == undefined) {
      MessageUtil.error({ summary: 'Re Password can not be empty' });
      return false;
    } else if (password != rePassword) {
      MessageUtil.error({ summary: 'These passwords not equals' });
      return false;
    } else if (password.replace(' ', '').length < 7) {
      MessageUtil.error({ summary: 'Password length must be greater then 7' });
      return false;
    } else if (password.replace(' ', '').length < 7) {
      MessageUtil.error({ summary: 'Re-Password length mus be greater then 7' });
      return false;
    } else {
      return true;
    }
  }

  public getQRData(): Promise<Record<string, any>> {
    return this.userClient.getQRData();
  }

  public login = async (payload: any) => {
    return this.userClient.login(payload);
  };

  public resetPassword = async (oldPassword: string, password: string) => {
    return this.userClient.resetPassword(oldPassword, password);
  };

  public logout = () => {
    return this.userClient.logout();
  };

  public saveAttributes = (attribute: UserSystemAttributes) => {
    return this.userClient.updateAttributes(attribute);
  };

  public changeActiveRiskType() {
    return this.userClient.changeActiveRiskType();
  }

  public readErrorLoginNotification() {
    return this.userClient.readErrorLoginNotification();
  }
}

export interface UserDto {
  password: string | undefined;
  role: 'Admin' | 'Member' | 'Root';
  emailValidation: boolean;
  rePassword: string | undefined;
  roles: Array<string>;
  groups: Array<string>;
  firstName: string | undefined;
  lastName: string | undefined;
  userType: 0 | 1;
  passwordValidation: boolean;
  email: string | undefined;
  username: string | undefined;
  preferences: UserPreferences;
  riskType: TransactionRiskType;
  activeRiskType: ActiveRiskType;
  filters?: DefaultFilter[];
}

export interface UserUpdateDto {
  password: string | undefined;
  emailValidation: boolean;
  rePassword: string | undefined;
  user: User;
}

export default UserService;
